
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from "@/integrations/supabase/client";
import { Image, ImagePlus, Loader2 } from 'lucide-react';

// Define the main category groups
const CATEGORY_GROUPS = {
  TUTORING: "과외&트레이닝",
  BUSINESS: "비즈니스",
  KOREAN_MART: "한인마트",
  REAL_ESTATE: "부동산",
  ATTORNEY: "변호사",
  OPENCHAT: "오픈톡방"
} as const;

type CategoryGroup = typeof CATEGORY_GROUPS[keyof typeof CATEGORY_GROUPS];

const formSchema = z.object({
  title: z.string().min(2, {
    message: "제목은 2글자 이상이어야 합니다.",
  }),
  description: z.string().min(10, {
    message: "설명은 10글자 이상이어야 합니다.",
  }),
  contact: z.string().min(5, {
    message: "연락처를 입력해주세요.",
  }),
  badges: z.string().optional(),
  mainCategory: z.enum([CATEGORY_GROUPS.TUTORING, CATEGORY_GROUPS.BUSINESS, CATEGORY_GROUPS.KOREAN_MART, CATEGORY_GROUPS.REAL_ESTATE, CATEGORY_GROUPS.ATTORNEY, CATEGORY_GROUPS.OPENCHAT], {
    required_error: "카테고리를 선택해주세요.",
  }),
  imageUrl: z.string().optional(),
});

interface BusinessPostFormProps {
  user: any;
}

export function BusinessPostForm({ user }: BusinessPostFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      contact: "",
      badges: "",
      imageUrl: "",
    },
  });

  const createBusinessListing = async (data: any) => {
    const { data: result, error } = await supabase
      .from('business_listings')
      .insert([data])
      .select('id')
      .single();
      
    if (error) throw error;
    return result;
  };

  const uploadImage = async (file: File): Promise<string> => {
    setIsUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('business_images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const imageUrl = await uploadImage(file);
      setUploadedImage(imageUrl);
      form.setValue('imageUrl', imageUrl);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "이미지 업로드 오류",
        description: "이미지를 업로드하는 중 오류가 발생했습니다.",
      });
    }
  };

  const mutation = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) => {
      const businessData = {
        title: values.title,
        category: values.mainCategory,
        description: values.description,
        contact: values.contact,
        badges: values.badges ? values.badges.split(',').map(tag => tag.trim()) : [],
        author_id: user?.id,
        author_name: user?.user_metadata?.full_name || user?.email,
        author_avatar: user?.user_metadata?.avatar_url,
        rating: 0,
        image_url: values.imageUrl || null,
      };
      return createBusinessListing(businessData);
    },
    onSuccess: () => {
      toast({
        title: "등록되었습니다",
        description: "한인업체/과외/오픈톡방 목록에 추가되었습니다.",
      });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      navigate('/business-tutoring');
    },
    onError: (error) => {
      console.error("Error creating business listing:", error);
      toast({
        variant: "destructive",
        title: "오류가 발생했습니다",
        description: "다시 시도해주세요.",
      });
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="mainCategory"
          render={({ field }) => (
            <FormItem>
              <FormLabel>카테고리</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="카테고리를 선택하세요" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={CATEGORY_GROUPS.TUTORING}>{CATEGORY_GROUPS.TUTORING}</SelectItem>
                  <SelectItem value={CATEGORY_GROUPS.BUSINESS}>{CATEGORY_GROUPS.BUSINESS}</SelectItem>
                  <SelectItem value={CATEGORY_GROUPS.KOREAN_MART}>{CATEGORY_GROUPS.KOREAN_MART}</SelectItem>
                  <SelectItem value={CATEGORY_GROUPS.REAL_ESTATE}>{CATEGORY_GROUPS.REAL_ESTATE}</SelectItem>
                  <SelectItem value={CATEGORY_GROUPS.ATTORNEY}>{CATEGORY_GROUPS.ATTORNEY}</SelectItem>
                  <SelectItem value={CATEGORY_GROUPS.OPENCHAT}>{CATEGORY_GROUPS.OPENCHAT}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>제목 / 업체명</FormLabel>
              <FormControl>
                <Input placeholder="제목 또는 업체명을 입력하세요" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>설명</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="상세한 설명을 입력하세요" 
                  className="min-h-[120px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="contact"
          render={({ field }) => (
            <FormItem>
              <FormLabel>연락처</FormLabel>
              <FormControl>
                <Input placeholder="전화번호, 이메일 또는 채팅앱 링크를 입력하세요" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="badges"
          render={({ field }) => (
            <FormItem>
              <FormLabel>태그 (쉼표로 구분)</FormLabel>
              <FormControl>
                <Input placeholder="예: 10년 경력, 한국어 가능, 무료 상담" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="imageUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>이미지</FormLabel>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    disabled={isUploading}
                    className="w-full"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <ImagePlus className="mr-2 h-4 w-4" />
                        이미지 업로드
                      </>
                    )}
                  </Button>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                    }}
                  />
                </div>
                
                {uploadedImage && (
                  <div className="relative border rounded-md overflow-hidden">
                    <img 
                      src={uploadedImage} 
                      alt="Uploaded preview" 
                      className="max-h-[200px] object-contain mx-auto" 
                    />
                  </div>
                )}
                
                <input type="hidden" {...field} />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-4 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate('/business-tutoring')}>
            취소
          </Button>
          <Button 
            type="submit" 
            disabled={mutation.isPending || isUploading}
            className="min-w-[80px]"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                등록 중...
              </>
            ) : "등록하기"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
